import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
} from "@mui/material";
import { SupportAPI } from "../api/support";
import { EnterprisesAPI } from "../api/enterprises";
import { getAuthToken } from "../utils/authSession";

export default function Footer() {
  const [openTerms, setOpenTerms] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [supportForm, setSupportForm] = useState({ reporter_name: "", reporter_email: "", enterprise_name: "", category: "GENERAL", subject: "", description: "" });
  const [supportError, setSupportError] = useState("");
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportReference, setSupportReference] = useState("");

  useEffect(() => {
    if (!openContact || !getAuthToken()) return;
    let active = true;
    EnterprisesAPI.getSession()
      .then((response) => {
        if (!active) return;
        const profile = response?.data?.data || {};
        setSupportForm((form) => ({
          ...form,
          reporter_name: form.reporter_name || profile.full_name || "",
          reporter_email: form.reporter_email || profile.email || "",
          enterprise_name: form.enterprise_name || profile.enterprise_name || "",
        }));
      })
      .catch(() => {
        // The support form remains usable for a signed-out or partially restored session.
      });
    return () => { active = false; };
  }, [openContact]);

  const closeSupport = () => {
    if (supportSaving) return;
    setOpenContact(false);
    setSupportError("");
    setSupportReference("");
  };

  const submitSupport = async (event) => {
    event.preventDefault();
    setSupportError("");
    setSupportSaving(true);
    try {
      const response = await SupportAPI.create(supportForm);
      setSupportReference(response?.data?.data?.reference || "Support request");
      setSupportForm({ reporter_name: "", reporter_email: "", enterprise_name: "", category: "GENERAL", subject: "", description: "" });
    } catch (error) {
      setSupportError(error?.response?.data?.error || "We could not send your support request. Please try again.");
    } finally {
      setSupportSaving(false);
    }
  };

  return (
    <>
      <Box
        component="footer"
        sx={{
    // Give the footer some separation from page content
    mt: 4,
          py: 2,
          // Make footer subtle: transparent background and no border
          backgroundColor: "transparent",
          borderTop: "none",
        }}
      >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              (c)2026 <b>Susha Services OPC Pvt. Ltd.</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Empowering Businesses with Smarter IT Asset Management
            </Typography>
          </Box>

          <Stack direction="row" spacing={3}>
            <Typography
              variant="body2"
              component="span"
              sx={{
                cursor: "pointer",
                color: "text.secondary",
                fontSize: 13,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => setOpenTerms(true)}
            >
              Terms & Conditions
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{
                cursor: "pointer",
                color: "text.secondary",
                fontSize: 13,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => { setSupportError(""); setSupportReference(""); setOpenContact(true); }}
            >
              Contact Support
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{
                cursor: "pointer",
                color: "text.secondary",
                fontSize: 13,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => setOpenPrivacy(true)}
            >
              Privacy Policy
            </Typography>
          </Stack>
        </Stack>
      </Container>
      </Box>

      {/* Terms Dialog */}
      <Dialog open={openTerms} onClose={() => setOpenTerms(false)} fullWidth maxWidth="sm">
        <DialogTitle>Terms of Service</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            These Terms of Service ("Terms") govern your use of the services provided by Susha Services OPC Pvt Ltd ("Company", "we", "our", or "us").
            By accessing or using our services, you agree to be bound by these Terms.
          </Typography>

          <Typography variant="body2" paragraph>
            1. Use of Services
            You must follow all policies made available to you within the services. Do not misuse our services or interfere with them.
          </Typography>

          <Typography variant="body2" paragraph>
            2. Accounts
            You are responsible for maintaining the confidentiality of your account information and for all activities under your account.
          </Typography>

          <Typography variant="body2" paragraph>
            3. Payments
            If applicable, you agree to pay all fees associated with the services in accordance with our billing terms.
          </Typography>

          <Typography variant="body2" paragraph>
            4. Intellectual Property
            All rights, title, and interest in and to the services are and will remain the exclusive property of Susha Services OPC Pvt Ltd and its licensors.
          </Typography>

          <Typography variant="body2" paragraph>
            5. Termination
            We may suspend or terminate your access to services at our discretion, without prior notice, for conduct that violates these Terms.
          </Typography>

          <Typography variant="body2" paragraph>
            6. Limitation of Liability
            To the maximum extent permitted by law, Susha Services OPC Pvt Ltd shall not be liable for any indirect, incidental, or consequential damages arising from the use of services.
          </Typography>

          <Typography variant="body2" paragraph>
            7. Governing Law
            These Terms shall be governed by and construed in accordance with the laws of India.
          </Typography>

          <Typography variant="body2" paragraph>
            If you do not agree to these Terms, you may discontinue using our services immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTerms(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Dialog */}
      <Dialog open={openPrivacy} onClose={() => setOpenPrivacy(false)} fullWidth maxWidth="sm">
        <DialogTitle>Privacy Policy</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            This Privacy Policy describes how Susha Services OPC Pvt Ltd ("Company", "we", "our", or "us") collects, uses, and protects your information.
          </Typography>

          <Typography variant="body2" paragraph>
            1. Information We Collect
            We may collect personal information such as your name, email address, and payment information, as well as non-personal information such as device details and usage data.
          </Typography>

          <Typography variant="body2" paragraph>
            2. How We Use Information
            We use the collected information to provide, improve, and personalize our services, process payments, and communicate with you.
          </Typography>

          <Typography variant="body2" paragraph>
            3. Sharing of Information
            We do not sell or rent your personal information to third parties.
          </Typography>

          <Typography variant="body2" paragraph>
            4. Data Security
            We implement reasonable technical and organizational measures to protect your information from unauthorized access, disclosure, or misuse.
          </Typography>

          <Typography variant="body2" paragraph>
            5. Cookies and Tracking
            We may use cookies and similar technologies to enhance your experience and analyze usage patterns.
          </Typography>

          <Typography variant="body2" paragraph>
            6. Your Rights
            You may request access to, correction of, or deletion of your personal information, subject to applicable laws.
          </Typography>

          <Typography variant="body2" paragraph>
            7. Changes to This Policy
            We may update this Privacy Policy from time to time. Continued use of our services indicates your acceptance of the changes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrivacy(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={openContact} onClose={closeSupport} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        {supportReference ? (
          <>
            <DialogTitle>Support request received</DialogTitle>
            <DialogContent dividers>
              <Alert severity="success" sx={{ mb: 2 }}>Reference: <strong>{supportReference}</strong></Alert>
              <Typography variant="body2" color="text.secondary">Your request has been sent to the Susha support team. You can continue working while the team reviews it.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}><Button variant="contained" onClick={closeSupport}>Done</Button></DialogActions>
          </>
        ) : (
          <Box component="form" onSubmit={submitSupport}>
            <DialogTitle sx={{ pb: 1 }}>Contact Support</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Tell us what you need. We will send your request to the Susha support team and use your email for follow-up.</Typography>
              {supportError ? <Alert severity="error" sx={{ mb: 2 }}>{supportError}</Alert> : null}
              <Stack spacing={2}>
                <TextField required size="small" label="Your name" value={supportForm.reporter_name} onChange={(event) => setSupportForm((form) => ({ ...form, reporter_name: event.target.value }))} autoComplete="name" />
                <TextField required size="small" type="email" label="Work email" value={supportForm.reporter_email} onChange={(event) => setSupportForm((form) => ({ ...form, reporter_email: event.target.value }))} autoComplete="email" />
                <TextField size="small" label="Enterprise / company" value={supportForm.enterprise_name} onChange={(event) => setSupportForm((form) => ({ ...form, enterprise_name: event.target.value }))} autoComplete="organization" />
                <TextField select required size="small" label="What do you need help with?" value={supportForm.category} onChange={(event) => setSupportForm((form) => ({ ...form, category: event.target.value }))}>
                  <MenuItem value="GENERAL">General question</MenuItem>
                  <MenuItem value="ACCOUNT">Account or access</MenuItem>
                  <MenuItem value="AGENT">Agent or device monitoring</MenuItem>
                  <MenuItem value="ASSETS">Asset management</MenuItem>
                  <MenuItem value="CLOUD">Cloud connection</MenuItem>
                  <MenuItem value="REPORTS">Reports or audit</MenuItem>
                  <MenuItem value="BILLING">Billing or subscription</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>
                <TextField required size="small" label="Short summary" value={supportForm.subject} onChange={(event) => setSupportForm((form) => ({ ...form, subject: event.target.value }))} inputProps={{ maxLength: 180 }} helperText="For example: Device is not appearing in Monitor" />
                <TextField required multiline minRows={4} label="How can we help?" value={supportForm.description} onChange={(event) => setSupportForm((form) => ({ ...form, description: event.target.value }))} inputProps={{ maxLength: 5000 }} helperText="Include the asset or device name and what you expected to happen, if relevant." />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button disabled={supportSaving} onClick={closeSupport}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={supportSaving} startIcon={supportSaving ? <CircularProgress size={16} color="inherit" /> : null}>{supportSaving ? "Sending…" : "Send request"}</Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </>
  );
}

// Terms Dialog and Contact Dialog are rendered inside Footer so they share theme/portal
// (kept local to this file for simplicity).

